<template>
  <v-card id="customise-sales-report">
    <v-card-title class="i-dialog-header-div">
      <span class="g-bold g-heading-m">Customise Report</span>
    </v-card-title>

    <v-card-text
      class="mt-6 mb-4"
      :class="{ 'i-dialog-content-div-large': $vuetifyDisplay.mdAndUp }"
    >
      <p class="g-bold g-heading-xs g-text-color-black">Target Type</p>
      <legacy-radio-group
        name="customise-sales-report-target-type"
        :default-col-props="{ cols: '6', class: 'pb-0' }"
        :options="[
          {
            label:
              reportType === 'BY_PRODUCTS'
                ? 'All Products'
                : `All ${isILR ? 'Customers' : 'Retailers'}`,
            value: ALL
          },
          { label: 'Banner Group(s)', value: BANNER_GROUP },
          {
            label: 'Multi-Store Operator(s)',
            value: MULTI_STORE_OPERATOR
          },
          {
            label: isILR ? 'Customer(s)' : 'Retailer(s)',
            value: RETAILER_STORE
          }
        ]"
        v-model="mutableReportFields.targetType"
        @change="resetTargetTypeForm"
      />

      <template v-if="mutableReportFields.targetType === BANNER_GROUP">
        <p class="g-bold g-heading-xs g-text-color-black mt-5 mt-md-7">
          Banner Group(s)
        </p>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-2 mb-6"
          clearable
          deletable-chips
          dense
          :items="targetBannerGroups"
          label="Select Banner Group(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || 'Banner Group(s) required']"
          v-model:search-input="searchInput"
          small-chips
          v-model="mutableReportFields.bannerGroupIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="mutableReportFields.targetType === MULTI_STORE_OPERATOR">
        <p class="g-bold g-heading-xs g-text-color-black mt-5 mt-md-7">
          Multi-Store Operator(s)
        </p>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-2 mb-6"
          clearable
          deletable-chips
          dense
          :items="targetMultiStoreOperators"
          label="Select Multi-Store Operator(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || 'Multi-Store Operator(s) required']"
          v-model:search-input="searchInput"
          small-chips
          v-model="mutableReportFields.multiStoreOperatorIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="mutableReportFields.targetType === RETAILER_STORE">
        <p class="g-bold g-heading-xs g-text-color-black mt-5 mt-md-7">
          {{ isILR ? 'Customer(s)' : 'Retailer(s)' }}
        </p>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-2 mb-9"
          clearable
          deletable-chips
          dense
          hide-details
          :items="targetStores"
          :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
          multiple
          outlined
          :rules="[
            (v) =>
              !!v.length || `${isILR ? 'Customer(s)' : 'Retailer(s)'} required`
          ]"
          v-model:search-input="searchInput"
          small-chips
          v-model="mutableReportFields.storeIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="mutableReportFields.targetType !== RETAILER_STORE">
        <p class="g-bold g-heading-xs g-text-color-black mt-6">Geography</p>
        <div class="mb-6">
          <legacy-radio-group
            name="customise-sales-report-geography"
            :default-col-props="{ cols: '4', class: 'pb-0' }"
            :options="[
              { label: 'National', value: NATIONAL },
              { label: 'State(s)', value: STATE }
            ]"
            v-model="mutableReportFields.targetGeography"
            @change="resetTargetGeographyForm"
          />
        </div>

        <template v-if="mutableReportFields.targetGeography === STATE">
          <p class="g-bold g-heading-xs g-text-color-black mt-2">State(s)</p>
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-10"
            clearable
            deletable-chips
            dense
            hide-details
            :items="targetStates"
            label="Select State(s)"
            multiple
            outlined
            :rules="[
              (v) => !!v.length || 'State(s) required for State rebates'
            ]"
            v-model:search-input="searchInput"
            small-chips
            v-model="mutableReportFields.deliveryAddressStates"
            @change="searchInput = ''"
          ></v-autocomplete>
        </template>
      </template>

      <p class="g-bold g-heading-xs g-text-color-black mt-2 mt-md-4">
        Date Range
        <v-icon
          class="i-tooltip-color pb-1"
          small
          v-tooltip.right="
            'Starts at 12:00am & Ends at 11:59pm on the submitted dates'
          "
        >
          mdi-help-circle-outline
        </v-icon>
      </p>
      <v-row align="center" class="mt-1">
        <v-col cols="12" sm="6">
          <v-menu
            v-model="dateFromMenu"
            :close-on-content-click="false"
            :nudge-right="15"
            :nudge-top="30"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                append-icon="mdi-calendar"
                background-color="#0000000f"
                class="mt-0"
                dense
                label="Start Date *"
                outlined
                readonly
                :rules="[
                  (v) => !!v || 'Start Date required',
                  () =>
                    beforeDateTo || 'End Date must be on or after Start Date'
                ]"
                v-on="on"
                v-model="dateFromDisplay"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="mutableReportFields.dateFrom"
              :min="formattedMinAllowedDate"
              :max="formattedMaxAllowedDate"
              @change="dateFromMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="12" sm="6">
          <v-menu
            v-model="dateToMenu"
            :close-on-content-click="false"
            :nudge-right="15"
            :nudge-top="30"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                append-icon="mdi-calendar"
                background-color="#0000000f"
                dense
                label="End Date *"
                outlined
                readonly
                :rules="[
                  (v) => !!v || 'End Date required',
                  () =>
                    beforeDateTo || 'End Date must be on or after Start Date'
                ]"
                v-on="on"
                v-model="dateToDisplay"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="mutableReportFields.dateTo"
              :min="formattedMinAllowedDate"
              :max="formattedMaxAllowedDate"
              @change="dateToMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <v-row class="mt-n4" align="center">
        <v-col cols="12" sm="6">
          <p class="g-bold g-heading-xs g-text-color-black">
            Aggregation Period
          </p>
          <v-select
            attach
            background-color="#0000000f"
            class="mt-4"
            dense
            :items="aggregationPeriods"
            label="Aggregation Period *"
            outlined
            :rules="[(v) => !!v || 'Aggregation Period selection required']"
            v-model="mutableReportFields.aggregationPeriod"
          ></v-select>
        </v-col>

        <v-col cols="12" sm="6">
          <p class="g-bold g-heading-xs g-text-color-black">Sales By</p>
          <v-select
            attach
            background-color="#0000000f"
            class="mt-4"
            dense
            :items="valuesAs"
            label="Sales By *"
            outlined
            :rules="[(v) => !!v || 'Sales By selection required']"
            v-model="mutableReportFields.valuesAs"
          ></v-select>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="pt-6 i-card-actions-div">
      <v-spacer></v-spacer>
      <v-btn
        color="blue darken-1"
        class="btn"
        text
        large
        rounded
        @click="$emit('cancel')"
        >Cancel</v-btn
      >
      <v-btn
        color="secondary"
        class="btn mr-4"
        large
        rounded
        :disabled="!validForm"
        @click="handleCustomise"
        >Customise</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapState, mapGetters } from 'vuex'
import LegacyRadioGroup from '@/components/LegacyRadioGroup.vue'

const MAX_DAYS_BACK = 365

const AGGREGATION_PERIODS = [
  { text: 'Week', value: 'WEEK' },
  { text: 'Month', value: 'MONTH' },
  { text: 'Quarter', value: 'QUARTER' },
  { text: 'Year', value: 'YEAR' }
]

const TARGET_TYPE = {
  ALL: 'ALL',
  BANNER_GROUP: 'BANNER_GROUP',
  MULTI_STORE_OPERATOR: 'MULTI_STORE_OPERATOR',
  RETAILER_STORE: 'RETAILER_STORE'
}

const TARGET_GEOGRAPHY = {
  NATIONAL: 'NATIONAL',
  STATE: 'STATE'
}

const REVENUE_VALUE = 'REVENUE'
const CARTONS_VALUE = 'CARTONS'

export default {
  components: {
    LegacyRadioGroup
  },

  props: {
    reportFields: {
      type: Object,
      required: true
    },
    reportType: {
      type: String,
      required: true
    }
  },

  data: () => ({
    aggregationPeriods: AGGREGATION_PERIODS,
    dateFromMenu: false,
    dateToMenu: false,
    originalReportFields: {},
    mutableReportFields: {},
    maxAllowedDate: moment(),
    minAllowedDate: moment().subtract(MAX_DAYS_BACK, 'days'),
    saving: false,
    searchInput: '',
    targetBannerGroups: [],
    targetMultiStoreOperators: [],
    targetStores: [],
    targetStates: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
    ...TARGET_TYPE,
    ...TARGET_GEOGRAPHY
  }),

  computed: {
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('stores', ['retailerStores']),
    ...mapGetters('tenant', ['isILR', 'tenantCurrency']),

    changesMade() {
      return !_.isEqual(this.mutableReportFields, this.originalReportFields)
    },

    beforeDateTo() {
      return (
        this.mutableReportFields.dateFrom <= this.mutableReportFields.dateTo
      )
    },

    dateFromDisplay() {
      return this.$options.filters.formatDatePicker(
        this.mutableReportFields.dateFrom
      )
    },

    dateToDisplay() {
      return this.$options.filters.formatDatePicker(
        this.mutableReportFields.dateTo
      )
    },

    formattedMinAllowedDate() {
      return this.formatDate(this.minAllowedDate)
    },

    formattedMaxAllowedDate() {
      return this.formatDate(this.maxAllowedDate)
    },

    validForm() {
      return (
        this.validTargetType &&
        this.validTargetGeography &&
        this.validDateRange &&
        this.changesMade
      )
    },

    validTargetType() {
      return (
        this.mutableReportFields.targetType === TARGET_TYPE.ALL ||
        (this.mutableReportFields.targetType === TARGET_TYPE.BANNER_GROUP &&
          !!this.mutableReportFields.bannerGroupIds.length) ||
        (this.mutableReportFields.targetType ===
          TARGET_TYPE.MULTI_STORE_OPERATOR &&
          !!this.mutableReportFields.multiStoreOperatorIds.length) ||
        (this.mutableReportFields.targetType === TARGET_TYPE.RETAILER_STORE &&
          !!this.mutableReportFields.storeIds.length)
      )
    },

    validTargetGeography() {
      return (
        this.mutableReportFields.targetGeography ===
          TARGET_GEOGRAPHY.NATIONAL ||
        (this.mutableReportFields.targetGeography === TARGET_GEOGRAPHY.STATE &&
          !!this.mutableReportFields.deliveryAddressStates.length)
      )
    },

    validDateRange() {
      return this.beforeDateTo
    },

    valuesAs() {
      return [
        { text: 'Carton Quantity', value: CARTONS_VALUE },
        { text: `Revenue (${this.tenantCurrency})`, value: REVENUE_VALUE }
      ]
    }
  },

  methods: {
    determineTargetType(reportFields) {
      if (reportFields.bannerGroupIds.length > 0)
        return TARGET_TYPE.BANNER_GROUP
      if (reportFields.multiStoreOperatorIds.length > 0)
        return TARGET_TYPE.MULTI_STORE_OPERATOR
      if (reportFields.storeIds.length > 0) return TARGET_TYPE.RETAILER_STORE
      else return TARGET_TYPE.ALL
    },

    determineTargetGeography(reportFields) {
      if (reportFields.deliveryAddressStates.length > 0)
        return TARGET_GEOGRAPHY.STATE
      else return TARGET_GEOGRAPHY.NATIONAL
    },

    formatDate(moment) {
      return moment.format('YYYY-MM-DD')
    },

    handleCustomise() {
      // Eliminate targetType and targeteography as not part of API but only UI
      const { targetType, targetGeography, ...filteredFields } =
        this.mutableReportFields
      this.$emit('customise', filteredFields)
    },

    // Map property names so that it works with V-select component
    mapForTargetList(list) {
      return list
        .map((element) => ({
          text: element.displayName,
          value: element.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    mapReportData(reportFields) {
      return {
        ...reportFields,
        targetType: this.determineTargetType(reportFields),
        targetGeography: this.determineTargetGeography(reportFields)
      }
    },

    resetTargetTypeForm() {
      this.mutableReportFields.bannerGroupIds = []
      this.mutableReportFields.multiStoreOperatorIds = []
      this.mutableReportFields.storeIds = []
      if (this.mutableReportFields.targetType === this.RETAILER_STORE)
        this.mutableReportFields.targetGeography = this.NATIONAL
    },

    resetTargetGeographyForm() {
      this.mutableReportFields.deliveryAddressStates = []
    }
  },

  mounted() {
    this.targetBannerGroups = this.mapForTargetList(this.bannerGroups)
    this.targetMultiStoreOperators = this.mapForTargetList(
      this.multiStoreOperators
    )
    this.targetStores = this.mapForTargetList(this.retailerStores)
    this.mutableReportFields = this.mapReportData(this.reportFields)
    this.originalReportFields = _.cloneDeep(this.mutableReportFields)
  }
}
</script>

<style lang="scss" scoped>
#customise-sales-report {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;
  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
}
</style>
